import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const GOLD = '#D4AF37';
const GOLD2 = '#f5c842';
const BG_CARD = '#0d1117';
const BG_CARD2 = '#111827';
const BORDER = 'rgba(212,175,55,0.12)';
const GREEN = '#10b981';
const RED = '#ef4444';
const BLUE = '#3b82f6';
const PURPLE = '#8b5cf6';
const ORANGE = '#f59e0b';

function fmt$(n) { return `$${parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }
function fmtDate(d) { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }

/* ── Big stat card ── */
function KPICard({ label, value, sub, color = GOLD, icon }) {
  return (
    <div style={{
      background: BG_CARD,
      border: `1px solid ${BORDER}`,
      borderRadius: 16,
      padding: '20px 22px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color}80, ${color})`,
      }} />
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ color: '#6b7280', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>{label}</div>
      <div style={{ color, fontWeight: 900, fontSize: 28, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</div>
      {sub && <div style={{ color: '#4b5563', fontSize: 11, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

/* ── Horizontal bar with animated fill ── */
function HorizBar({ label, value, maxVal, color = GOLD, suffix = '' }) {
  const pct = maxVal > 0 ? Math.min((value / maxVal) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ color: '#d1d5db', fontSize: 13, fontWeight: 600 }}>{label}</span>
        <span style={{ color, fontSize: 13, fontWeight: 700 }}>{suffix}{value}</span>
      </div>
      <div style={{ height: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: `linear-gradient(90deg, ${color}60, ${color})`,
          borderRadius: 4,
          transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
          boxShadow: `0 0 8px ${color}60`,
        }} />
      </div>
    </div>
  );
}

/* ── Vertical bar chart ── */
function BarChart({ data, valueKey = 'revenue', labelKey = 'date', formatLabel, formatValue, color = GOLD, height = 140 }) {
  if (!data || data.length === 0) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', fontSize: 13 }}>
      No data yet
    </div>
  );
  const max = Math.max(...data.map(d => parseFloat(d[valueKey]) || 0), 1);
  const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height, paddingBottom: 0 }}>
        {data.map((d, i) => {
          const val = parseFloat(d[valueKey]) || 0;
          const pct = (val / max) * 100;
          const label = formatLabel ? formatLabel(d) : d[labelKey];
          const isToday = label === today;
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end', cursor: 'default' }}
              title={`${label}: ${formatValue ? formatValue(val) : val}`}>
              <div style={{
                width: '100%',
                height: `${Math.max(pct, val > 0 ? 3 : 1)}%`,
                background: isToday
                  ? `linear-gradient(180deg, ${GOLD2}, ${GOLD})`
                  : `linear-gradient(180deg, ${color}80, ${color}40)`,
                borderRadius: '3px 3px 0 0',
                minHeight: val > 0 ? 3 : 1,
                boxShadow: isToday ? `0 0 10px ${GOLD}80` : 'none',
                transition: 'height 0.6s ease',
              }} />
            </div>
          );
        })}
      </div>
      {/* X-axis labels */}
      <div style={{ display: 'flex', gap: 3, marginTop: 6 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 8, color: '#374151', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {i % 2 === 0 ? (formatLabel ? formatLabel(d) : d[labelKey]) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Status pill ── */
function StatusPill({ label, value, color }) {
  return (
    <div style={{
      background: `${color}10`,
      border: `1px solid ${color}30`,
      borderRadius: 10,
      padding: '12px 16px',
      textAlign: 'center',
      flex: 1,
      minWidth: 70,
    }}>
      <div style={{ color, fontWeight: 900, fontSize: 24, lineHeight: 1 }}>{value || 0}</div>
      <div style={{ color: '#6b7280', fontSize: 10, marginTop: 4, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

/* ── Section header ── */
function SectionHeader({ title, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, fontWeight: 800, fontSize: 14, color: '#fff', letterSpacing: '0.03em' }}>{title}</h3>
      {sub && <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/* ── Card wrapper ── */
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: BG_CARD,
      border: `1px solid ${BORDER}`,
      borderRadius: 16,
      padding: '20px',
      marginBottom: 16,
      ...style,
    }}>
      {children}
    </div>
  );
}

const TABS = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'clients', label: 'Clients' },
];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('revenue');

  useEffect(() => {
    api.get('/analytics')
      .then(r => setData(r.data))
      .catch(() => setError('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <div style={{ color: GOLD, fontSize: 13 }}>Loading analytics…</div>
      </div>
    </AdminLayout>
  );
  if (error) return <AdminLayout><div style={{ textAlign: 'center', padding: 80, color: RED }}>{error}</div></AdminLayout>;

  const totals = data.totals;
  const completionRate = parseFloat(totals.completion_rate || 0);

  const tabStyle = (active) => ({
    padding: '10px 18px',
    background: active ? `${GOLD}15` : 'none',
    border: 'none',
    borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
    color: active ? GOLD : '#6b7280',
    fontWeight: active ? 700 : 500,
    fontSize: 13,
    cursor: 'pointer',
    borderRadius: active ? '4px 4px 0 0' : 0,
    transition: 'all 0.2s',
  });

  return (
    <AdminLayout>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Analytics</h1>
        <div style={{ color: '#4b5563', fontSize: 12, marginTop: 4 }}>Performance overview · Updated now</div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER}`, marginBottom: 24, marginLeft: -24, marginRight: -24, paddingLeft: 24 }}>
        {TABS.map(t => <button key={t.key} style={tabStyle(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
      </div>

      {/* ── REVENUE TAB ── */}
      {tab === 'revenue' && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
            <KPICard icon="$" label="Today" value={fmt$(totals.daily_revenue)} sub="completed today" color={GOLD} />
            <KPICard icon="7d" label="This Week" value={fmt$(totals.weekly_revenue)} sub="last 7 days" color={BLUE} />
            <KPICard icon="Mo" label="This Month" value={fmt$(totals.monthly_revenue)} sub="current month" color={GREEN} />
            <KPICard icon="★" label="All Time" value={fmt$(totals.total_revenue)} sub="completed only" color={PURPLE} />
          </div>

          {/* Completion rate */}
          <Card>
            <SectionHeader title="Completion Rate" sub="% of appointments completed vs total" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
              <div style={{
                fontSize: 42, fontWeight: 900, lineHeight: 1,
                background: `linear-gradient(135deg, ${GOLD}, ${GOLD2})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {completionRate}%
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ height: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 5, overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(completionRate, 100)}%`, height: '100%',
                    background: `linear-gradient(90deg, ${GOLD}60, ${GOLD2})`,
                    borderRadius: 5,
                    boxShadow: `0 0 12px ${GOLD}60`,
                  }} />
                </div>
                <div style={{ color: '#4b5563', fontSize: 11, marginTop: 6 }}>
                  {totals.completed_count || 0} completed · {totals.pending_count || 0} pending · {totals.confirmed_count || 0} confirmed
                </div>
              </div>
            </div>

            {/* Status pills */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill label="Pending" value={totals.pending_count} color={ORANGE} />
              <StatusPill label="Confirmed" value={totals.confirmed_count} color={BLUE} />
              <StatusPill label="Completed" value={totals.completed_count} color={GREEN} />
              <StatusPill label="Cancelled" value={parseInt(totals.cancelled_count || 0) + parseInt(totals.late_cancelled_count || 0)} color="#6b7280" />
              <StatusPill label="No-Show" value={totals.no_show_count} color={RED} />
            </div>
          </Card>

          {/* Revenue bar chart */}
          <Card>
            <SectionHeader title="Revenue — Last 14 Days" sub="Daily earnings from completed appointments" />
            <BarChart data={data.daily} valueKey="revenue" formatLabel={d => fmtDate(d.date)} formatValue={v => fmt$(v)} color={GOLD} height={140} />
          </Card>

          {/* Top services */}
          <Card>
            <SectionHeader title="Top Services" sub="Ranked by total revenue" />
            {!data.topServices?.length ? (
              <div style={{ color: '#374151', fontSize: 13, textAlign: 'center', padding: 20 }}>No data yet</div>
            ) : (
              data.topServices.slice(0, 6).map((s, i) => (
                <HorizBar
                  key={s.name}
                  label={`${i + 1}. ${s.name} (${s.booking_count})`}
                  value={fmt$(s.revenue).replace('$', '')}
                  maxVal={parseFloat(data.topServices[0]?.revenue || 1)}
                  color={i === 0 ? GOLD : i === 1 ? '#9ca3af' : i === 2 ? '#d97706' : BLUE}
                  suffix="$"
                />
              ))
            )}
          </Card>

          {/* Busiest days */}
          <Card>
            <SectionHeader title="Busiest Days of the Week" sub="Average appointments per day" />
            {!data.busiestDays?.length ? (
              <div style={{ color: '#374151', fontSize: 13, textAlign: 'center', padding: 20 }}>No data yet</div>
            ) : (
              data.busiestDays.map(d => (
                <HorizBar
                  key={d.day_of_week}
                  label={d.day_name}
                  value={d.count}
                  maxVal={Math.max(...data.busiestDays.map(x => x.count), 1)}
                  color={PURPLE}
                  suffix=""
                />
              ))
            )}
          </Card>
        </>
      )}

      {/* ── CLIENTS TAB ── */}
      {tab === 'clients' && (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
            <KPICard icon="" label="Total Clients" value={data.retention?.total_clients || 0} color={GOLD} />
            <KPICard icon="" label="Returning" value={data.retention?.returning_clients || 0}
              sub={data.retention?.total_clients > 0 ? `${Math.round((data.retention.returning_clients / data.retention.total_clients) * 100)}% retention` : ''}
              color={GREEN} />
            <KPICard icon="" label="Avg Visit Gap" value={data.avgDaysBetweenVisits ? `${data.avgDaysBetweenVisits}d` : '—'} sub="days between visits" color={BLUE} />
            <KPICard icon="" label="At Risk" value={data.atRisk?.length || 0} sub="28–90 days inactive"
              color={data.atRisk?.length > 0 ? ORANGE : GREEN} />
          </div>

          {/* New clients chart */}
          <Card>
            <SectionHeader title="New Clients — Last 6 Months" sub="Growth trend" />
            <BarChart data={data.newClientsMonthly} valueKey="new_clients" formatLabel={d => d.month} formatValue={v => `${v} clients`} color={GREEN} height={130} />
          </Card>

          {/* Top clients */}
          <Card>
            <SectionHeader title="Top Clients by Spend" />
            {!data.topClients?.length ? (
              <div style={{ color: '#374151', fontSize: 13, textAlign: 'center', padding: 20 }}>No completed appointments yet.</div>
            ) : (
              <>
                {/* Spend bars */}
                {data.topClients.slice(0, 8).map((c, i) => (
                  <HorizBar
                    key={c.id}
                    label={`${i === 0 ? '★ ' : ''}${c.name} (${c.visit_count} visits)`}
                    value={fmt$(c.total_spent).replace('$', '')}
                    maxVal={parseFloat(data.topClients[0]?.total_spent || 1)}
                    color={i === 0 ? GOLD : BLUE}
                    suffix="$"
                  />
                ))}
                {/* Detail table */}
                <div style={{ overflowX: 'auto', marginTop: 16 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                        {['Client', 'Visits', 'Spent', 'Last Visit', 'No-Shows'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', color: '#4b5563', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.topClients.map((c, i) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <td style={{ padding: '10px 10px' }}>
                            <Link to={`/dash/clients/${c.id}`} style={{ color: '#fff', fontWeight: 600, textDecoration: 'none' }}>
                              {i === 0 && <span style={{ color: GOLD, marginRight: 4 }}>★</span>}{c.name}
                            </Link>
                          </td>
                          <td style={{ padding: '10px 10px', color: '#9ca3af' }}>{c.visit_count}</td>
                          <td style={{ padding: '10px 10px', color: GOLD, fontWeight: 700 }}>{fmt$(c.total_spent)}</td>
                          <td style={{ padding: '10px 10px', color: '#6b7280' }}>{c.last_visit ? fmtDate(c.last_visit) : '—'}</td>
                          <td style={{ padding: '10px 10px' }}>
                            {parseInt(c.no_show_count) > 0
                              ? <span style={{ color: RED, fontWeight: 700 }}>{c.no_show_count}</span>
                              : <span style={{ color: '#374151' }}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          {/* At-risk clients */}
          <Card>
            <SectionHeader title="At-Risk Clients" sub="Haven't booked in 28–90 days" />
            {!data.atRisk?.length ? (
              <div style={{ textAlign: 'center', padding: 24, color: GREEN, fontSize: 13 }}>
                ✓ All clear — no at-risk clients right now
              </div>
            ) : (
              data.atRisk.map(c => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
                  flexWrap: 'wrap', gap: 8,
                }}>
                  <div>
                    <Link to={`/dash/clients/${c.id}`} style={{ color: '#fff', fontWeight: 600, textDecoration: 'none', fontSize: 14 }}>{c.name}</Link>
                    {c.phone && <div style={{ color: '#4b5563', fontSize: 11, marginTop: 2 }}>{c.phone}</div>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: ORANGE, fontWeight: 700, fontSize: 13 }}>{c.days_since}d ago</div>
                      <div style={{ color: '#4b5563', fontSize: 11 }}>{c.total_visits} visits</div>
                    </div>
                    <a href={`sms:${c.phone}`} style={{
                      padding: '7px 14px', borderRadius: 8,
                      background: `${GOLD}15`, border: `1px solid ${GOLD}30`,
                      color: GOLD, fontSize: 12, fontWeight: 700, textDecoration: 'none',
                    }}>
                      Text →
                    </a>
                  </div>
                </div>
              ))
            )}
          </Card>
        </>
      )}
    </AdminLayout>
  );
}
